import { Badge } from '@chopchop-se/ui';
import type { BcLink } from '@warehouse/shared';
import { RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "Synkad från BC" (MODULES §M1).
 *
 * The label stays SWEDISH in every UI language: it names a state the warehouse
 * talks about out loud, the same way labels and printouts do (ARCHITECTURE §9).
 * Its explanations are translated; the badge itself is not.
 *
 * The badge is cosmetic — the server refuses the write regardless
 * (`E_CONFLICT { reason: 'bc_synced' }`), which is what makes rendering the
 * field disabled honest rather than decorative.
 */
export function BcBadge({ bc }: { bc: Pick<BcLink, 'synced' | 'bcNo'> }): ReactNode {
  const { t } = useTranslation('masterData');
  if (!bc.synced) return null;
  return (
    <Badge tone="info" icon={<RefreshCw className="size-3" aria-hidden />} title={bc.bcNo ?? undefined}>
      {t('bc.badge')}
      {bc.bcNo ? ` · ${bc.bcNo}` : ''}
    </Badge>
  );
}

/** The banner above a synced record's form. */
export function BcSyncNotice({ bc }: { bc: BcLink }): ReactNode {
  const { t } = useTranslation('masterData');
  if (!bc.synced) return null;
  return (
    <div
      className="mb-4 flex items-start gap-3 rounded-xl border border-surface-border bg-surface-subtle px-4 py-3"
      role="note"
    >
      <RefreshCw className="mt-0.5 size-4 shrink-0 text-ink-muted" aria-hidden />
      <div className="min-w-0 text-sm text-ink-light">
        <p>{t('bc.banner')}</p>
        {bc.bcNo ? (
          <p className="mt-1 font-mono text-xs text-ink-muted">
            {t('bc.number')}: {bc.bcNo}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** `true` when the field is one BC owns on this (synced) record. */
export function isBcLocked(bc: BcLink | undefined, field: string): boolean {
  return Boolean(bc?.synced && bc.syncedFields.includes(field));
}
